import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { useHistory, useParams } from "react-router";
import { Route, selectRoute } from "src/config/routes";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { ColumnIcon } from "src/components/columnIcon/ColumnIcon";
import styles from "./SplitPdf.module.css";
import { STATE } from "src/stores/krediflow/SplitPdfStore";

// Ensure proper worker is set up
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Params {
    fileid: string;
}

const SplitPdfBase = () => {
    const { t } = useTranslation();
    const { navStore, authStore, splitPdfStore } = useContext(RootStoreContext);

    const { fileid } = useParams<Params>();

    const [hasPdfLoaded, setHasPdfLoaded] = useState<boolean>(false);
    const [element, setElement] = React.useState<HTMLElement | null>(null);

    const history = useHistory();

    useEffect(() => {
        splitPdfStore.setHistory(history);
    }, [splitPdfStore, history]);

    useEffect(() => {
        navStore.setTitle(splitPdfStore.currentFile?.name ?? "");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        navStore.setBackbutton(
            `${selectRoute(Route.creditors, authStore.user?.role)}?tab=import`,
            t("screens.kredi_flow.sidebar_title").toString()
        );
    }, [navStore, t, authStore.user?.role, splitPdfStore.currentFile?.name]);

    useEffect(() => {
        if (fileid) {
            splitPdfStore.init(fileid);
        }

        // Load all invoices once component is rendered
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!element) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                splitPdfStore.setColWidth(entry.contentRect.left + entry.contentRect.right);
            }
        });

        resizeObserver.observe(element);
        return () => resizeObserver.unobserve(element);
    }, [element, splitPdfStore]);

    const onAdjustColumnsView = useCallback(
        (numberOfColumns: number) => () => {
            splitPdfStore.setNumberOfPagesPerRow(numberOfColumns);
        },
        [splitPdfStore]
    );

    const onDocumentLoadSuccess = useCallback(
        (document: any) => {
            setHasPdfLoaded(true);

            const numberOfPages = document.numPages;

            if (numberOfPages) {
                splitPdfStore.setNumberOfPages(numberOfPages);
            }
        },
        [splitPdfStore]
    );

    const handleBarClick = useCallback(
        (pageNumber: number) => () => {
            const newSplitPoints = splitPdfStore.splitPoints.includes(pageNumber)
                ? splitPdfStore.splitPoints.filter((p) => p !== pageNumber)
                : [...splitPdfStore.splitPoints, pageNumber];

            splitPdfStore.setSplitPoints(newSplitPoints.sort((a, b) => a - b));
        },
        [splitPdfStore]
    );

    const onSplitPdf = useCallback(async () => {
        await splitPdfStore.splitPdf();
    }, [splitPdfStore]);

    const splitPdfButtonLabel = (
        <div className="d-flex align-items-center">
            {splitPdfStore.currentState === STATE.SPLITTING_PDF && (
                <div>
                    <UI.RotatingSpinner noLogo size={20} />
                </div>
            )}

            <div>{t("screens.kredi_flow.pdf_split.title")}</div>
        </div>
    );

    return (
        <UI.Container className={"p-5"} fluid>
            <UI.Row className="mb-2">
                <UI.Col xs={12} sm={8} lg={4}>
                    <UI.Title>{t("screens.kredi_flow.pdf_split.title")}</UI.Title>
                </UI.Col>
                <UI.Col xs={12} sm={4} lg={8} className="text-right d-flex justify-content-end">
                    {/* 4-column icon */}
                    <ColumnIcon
                        columns={4}
                        selected={splitPdfStore.numberOfPagesPerRow === 4}
                        onClick={onAdjustColumnsView(4)}
                    />
                    {/* 2-column icon */}
                    <ColumnIcon
                        columns={2}
                        selected={splitPdfStore.numberOfPagesPerRow === 2}
                        onClick={onAdjustColumnsView(2)}
                    />
                    {/* 1-column icon */}
                    <ColumnIcon
                        columns={1}
                        selected={splitPdfStore.numberOfPagesPerRow === 1}
                        onClick={onAdjustColumnsView(1)}
                    />
                </UI.Col>
            </UI.Row>
            {splitPdfStore.currentFile?.url ? (
                <Document loading={""} file={splitPdfStore.currentFile.url} onLoadSuccess={onDocumentLoadSuccess}>
                    <UI.Row>
                        {Array.from({ length: splitPdfStore.numberOfPages }, (_, index) => (
                            <UI.Col
                                ref={index === 0 ? setElement : undefined}
                                key={index}
                                xs={12 / splitPdfStore.numberOfPagesPerRow}
                                className={"mb-4"}
                                id={`splitcol-${index}`}
                            >
                                <Page pageNumber={index + 1} width={splitPdfStore.colWidth * 0.9} />
                                {index < splitPdfStore.numberOfPages - 1 && (
                                    <SplitBar
                                        type={splitPdfStore.numberOfPagesPerRow !== 1 ? "vertical" : "horizontal"}
                                        isActive={splitPdfStore.splitPoints.includes(index + 1)}
                                        onClick={handleBarClick(index + 1)}
                                    />
                                )}
                            </UI.Col>
                        ))}
                    </UI.Row>
                </Document>
            ) : splitPdfStore.currentState === STATE.INIT ? (
                <div>{t("screens.kredi_flow.pdf_split.pdf_is_loading")}...</div>
            ) : splitPdfStore.currentState === STATE.ERROR_LOADING_FILE ? (
                <div>{t("screens.kredi_flow.pdf_split.error_loading_pdf")}</div>
            ) : null}
            {hasPdfLoaded && (
                <UI.Row>
                    <div className="mx-auto">
                        <UI.Button
                            className={`default-button-height ${styles.SplitPdfButton}`}
                            label={splitPdfButtonLabel}
                            disabled={
                                splitPdfStore.splitPoints.length === 0 ||
                                splitPdfStore.currentState === STATE.SPLITTING_PDF
                            }
                            onClick={onSplitPdf}
                        />
                    </div>
                </UI.Row>
            )}
        </UI.Container>
    );
};

export const SplitPdf = Sentry.withProfiler(observer(SplitPdfBase));

export interface SplitBarProps {
    type: "vertical" | "horizontal";
    isActive: boolean;
    onClick: () => void;
}

const SplitBarBase = (props: SplitBarProps) => {
    const { type, isActive, onClick } = props;

    return (
        <div
            className={`${styles.SplitBar} ${isActive ? styles.Active : ""} ${
                type === "vertical" ? styles.Vertical : styles.Horizontal
            }`}
            onClick={onClick}
        >
            {/* The dashed line */}
            <div className={styles.SplitBarLine} />
            {/* The scissors icon in the center */}
            <div className={styles.SplitBarIcon}>
                <UI.Icon icon={UI.SVGIcon.Scissors} size={16} className={styles.ScissorsIcon} />
            </div>
        </div>
    );
};

export const SplitBar = observer(SplitBarBase);
